/**
 * Axios HTTP client with interceptors for error handling
 * Auth is handled via x-user-slug header from localStorage
 */

import axios, { AxiosError } from 'axios';
import type { ErrorResponse } from '@/types/api';

// Use relative /api path — Vite proxy handles forwarding to the backend
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

const STORAGE_KEY = 'user-slug';

/**
 * Create Axios instance with default configuration
 */
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 seconds
});

/**
 * Request interceptor: Add x-user-slug header from localStorage
 */
apiClient.interceptors.request.use((config) => {
  const slug = localStorage.getItem(STORAGE_KEY);
  console.log('[API] slug from localStorage:', slug, '| url:', config.url);
  if (slug) {
    config.headers['x-user-slug'] = slug;
  }
  return config;
});

/**
 * Response interceptor: Handle common errors
 * Note: 401/403 errors are handled by components/queries, not forced logout
 */
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error: AxiosError<ErrorResponse>) => {
    // Handle network errors
    if (!error.response) {
      console.error('Network error:', error.message);
      return Promise.reject({
        error: 'Network error',
        message: 'Unable to connect to the server. Please check your internet connection.',
        code: 'NETWORK_ERROR',
      });
    }

    // Handle other errors
    const errorResponse: ErrorResponse = error.response?.data || {
      error: 'Unknown error',
      code: 'UNKNOWN_ERROR',
      statusCode: error.response?.status || 500,
    };

    console.error('API error:', errorResponse);
    return Promise.reject(errorResponse);
  }
);

/**
 * Generic API error class
 */
export class ApiError extends Error {
  public code: string;
  public statusCode: number;
  public details?: unknown;

  constructor(code: string, statusCode: number, details?: unknown) {
    super(`API Error: ${code}`);
    this.name = 'ApiError';
    this.code = code;
    this.statusCode = statusCode;
    this.details = details;
  }
}

/**
 * Transform Axios error to ApiError
 */
export function transformError(error: unknown): ApiError {
  if (error && typeof error === 'object' && 'code' in error) {
    const err = error as ErrorResponse;
    return new ApiError(err.code, err.statusCode, err.details);
  }
  return new ApiError('UNKNOWN_ERROR', 500, error);
}
